import { ReuseModal } from "../../ReuseModal";
import { Link } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import {
  Account,
  CRMData,
  IntegrationInfo,
  Mismatch,
} from "../../../../types/apiTypes";
import {
  accountsApi,
  campaignsApi,
  useCreateImportFromCRMMutation,
  useEnterCIDMutation,
  useEnterCompanyManuallyMutation,
  useGetCRMUrlMutation,
  useGetMismatchesQuery,
  useUploadCampaignFilesMutation,
} from "../../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import {
  CIDUploadMethod,
  UploadFromDesktopMethod,
} from "../../../products/CreateCampaignFlow/steps";
import { AppRoutes, Integration } from "../../../../types/enums";
import { Stack, Typography } from "@mui/material";
import { CustomButton } from "../../CustomButton";
import { PlusIcon, UpdateIcon, UploadDataIcon } from "../../../images";
import { MismatchDataCRMWrapper } from "./MismatchDataCRMWrapper";
import { MismatchDataDesktopWrapper } from "./MismatchDataDesktopWrapper";
import { MismatchDataModal } from "../MismatchDataModal";
import { CRMUpdateMethod } from "./CRMUpdateMethod";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { ResyncHeaderBlock } from "./ResyncHeaderBlock";
import { setImportDataStatus } from "../../../../features/companySlice";
import { EnterManually } from "../../../products/CreateCampaignFlow/steps/TargetAccountsStep/EnterManually";
import { NAME_REGEX, URL_REGEX } from "../../../../utils/regex";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  campaign_id: string;
  integration?: IntegrationInfo;
  mismatches: Mismatch;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
};

const methodLIst = {
  crm: "Sync CRM",
  desktop: "Upload File",
  manually: "Manually",
  cid: "Sync by CID",
};

export const ResyncDataModal = ({
  isOpen,
  setIsOpen,
  campaign_id,
  integration,
  mismatches,
  isLoading,
  setIsLoading,
}: Props) => {
  const dispatch = useAppDispatch();

  const [crmIntegration, setCrmIntegration] = useState<
    IntegrationInfo | undefined
  >(integration);
  const isLeadTools =
    !!crmIntegration && crmIntegration.integration === Integration.lead_tools;
  const [currentMethod, setCurrentMethod] = useState<string>(
    isLeadTools ? methodLIst.desktop : methodLIst.crm,
  );
  const [files, setFiles] = useState<File[]>([]);
  const [companies, setCompanies] = useState<Account[]>([]);
  const [dataSourceList, setDataSourceList] = useState<CRMData | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isMismatchModalOpen, setIsMismatchModalOpen] =
    useState<boolean>(false);
  const [cid, setCID] = useState<string>(
    isLeadTools ? crmIntegration.lead_tools_cid : "",
  );

  const [subCID, setSubCID] = useState<string[]>([]);

  const importDataStatus = useAppSelector(
    (state) => state.company.importDataStatus,
  );

  const [enterCompanyManually] = useEnterCompanyManuallyMutation();
  const { data } = useGetMismatchesQuery(campaign_id, {
    skip: mismatches?.total === 0,
    refetchOnMountOrArgChange: true,
  });

  const [uploadFile] = useUploadCampaignFilesMutation();
  const [importFromCRM] = useCreateImportFromCRMMutation();
  const [getCRMUrl] = useGetCRMUrlMutation();
  const [enterCID] = useEnterCIDMutation();

  const onDoneHandler = () => {
    dispatch(
      accountsApi.util.invalidateTags([
        "analytics",
        "export-view",
        "list-view",
        "filters",
      ]),
    );

    dispatch(
      campaignsApi.util.invalidateTags([
        "summary",
        "engagement-config",
        "Campaign",
        "members",
        "company",
      ]),
    );

    setIsLoading(false);
    setIsOpen(false);
  };

  useEffect(() => {
    setCrmIntegration(integration);
  }, [integration]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentMethod(isLeadTools ? methodLIst.desktop : methodLIst.crm);
      setFiles([]);
      setDataSourceList(null);
      setIsVisible(false);
      setIsMismatchModalOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (importDataStatus && campaign_id === importDataStatus.campaign_id) {
      if (importDataStatus.status === "failed") {
        dispatch(setImportDataStatus(null));
        setDataSourceList(null);
        toast.error(
          <CustomToast
            type="error"
            text={importDataStatus.message || "The company data upload failed."}
            title="Upload Failed"
          />,
        );
        setIsLoading(false);
      }

      if (importDataStatus.status === "completed") {
        dispatch(setImportDataStatus(null));
        onDoneHandler();
      }

      if (importDataStatus.status === "in_progress") {
        setIsLoading(true);
      }
    }
  }, [importDataStatus]);

  useEffect(() => {
    setIsVisible(isMismatchModalOpen);
  }, [isMismatchModalOpen]);

  const isFromDesktop =
    currentMethod === methodLIst.desktop && files.length > 0;
  const isFromCRM = currentMethod === methodLIst.crm && !!dataSourceList;
  const isFromManually =
    currentMethod === methodLIst.manually &&
    companies.length > 0 &&
    companies.every(
      ({ name, website_url }) =>
        name.trim() &&
        NAME_REGEX.test(name) &&
        (!website_url || URL_REGEX.test(website_url)),
    );

  const isCID = currentMethod === methodLIst.cid;

  const isNeedConnect =
    crmIntegration &&
    !crmIntegration?.is_connected &&
    currentMethod === methodLIst.crm;
  const isMismatch = mismatches.total > 0 && currentMethod === methodLIst.crm;

  const navigationMethods = [
    {
      label: methodLIst.crm,
      isDisable: isFromDesktop,
      hide: isLeadTools,
    },
    { label: methodLIst.desktop, isDisable: isFromDesktop, hide: false },
    { label: methodLIst.manually, isDisable: false, hide: false },
    {
      label: methodLIst.cid,
      isDisable: false,
      hide:
        crmIntegration && crmIntegration.integration !== Integration.lead_tools,
    },
  ];

  const methods: Record<string, ReactNode> = {
    [methodLIst.desktop]: (
      <MismatchDataDesktopWrapper
        isFirstUpload={true}
        setIsVisible={setIsVisible}
        isDisabled={isLoading}
        campaign_id={campaign_id}
      >
        <UploadFromDesktopMethod isLoading={isLoading} setFiles={setFiles} />
      </MismatchDataDesktopWrapper>
    ),
    [methodLIst.crm]: (
      <MismatchDataCRMWrapper
        mismatches={mismatches}
        isFirstUpload={!!crmIntegration}
      >
        <CRMUpdateMethod
          actionType="re-sync"
          synced={dataSourceList?.crm}
          setIsVisible={setIsVisible}
          setDataSource={setDataSourceList}
          campaign_id={campaign_id}
          crmIntegration={crmIntegration}
        />
      </MismatchDataCRMWrapper>
    ),
    [methodLIst.manually]: (
      <EnterManually setCompanies={setCompanies} isDisabled={isLoading} />
    ),
    [methodLIst.cid]: (
      <CIDUploadMethod
        cid={cid}
        currentSubCID={crmIntegration?.lead_tools_sub_cids || []}
        setCID={setCID}
        setSubCID={setSubCID}
        isLeadTools={isLeadTools}
        isLoading={isLoading}
      />
    ),
  };

  const headers: Record<string, ReactNode> = {
    [methodLIst.desktop]: (
      <ResyncHeaderBlock
        isFirstUpload={true}
        title="Upload Your Data for Buying Committee Insights"
        text={
          "Drag and drop your target accounts and contacts data to receive valuable buying committee insights" +
          " and engagement tracking. Gain access to external contacts search & update."
        }
        customText={
          <Typography component="span" variant="body1" color="#4C545B">
            Check{" "}
            <Link
              to={AppRoutes.CAMPAIGNS_REQUIREMENTS}
              style={{ textDecoration: "none" }}
              target="_blank"
            >
              <span style={{ color: "#0069C4" }}>Field requirements</span>
            </Link>{" "}
            to see the full list of accounts and contacts related fields.
          </Typography>
        }
        icon={<UploadDataIcon />}
      />
    ),
    [methodLIst.crm]: (
      <ResyncHeaderBlock
        isFirstUpload={true}
        title="Connect Your CRM"
        text="Utilize import & export operations, active updates tracking."
        icon={<UpdateIcon color="#1C3C6C" />}
      />
    ),
    [methodLIst.manually]: (
      <ResyncHeaderBlock
        isFirstUpload={true}
        title="Add Accounts Manually"
        text="Add target accounts names to search for buying committee and get roles segmentation insights."
        icon={<PlusIcon color="#1C3C6C" />}
      />
    ),
    [methodLIst.cid]: (
      <ResyncHeaderBlock
        isFirstUpload={!isLeadTools}
        title="Sync Data Using Campaign CID"
        text="Provide your CID to retrieve accounts and leads data. Get leads buying committee segmentation  insights."
        icon={<UpdateIcon color="#1C3C6C" />}
      />
    ),
  };

  const submitFromDesktopHandler = async (campaign_id: string) => {
    await uploadFile({
      campaign_id,
      files,
      import_contacts: true,
    })
      .unwrap()
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Upload Failed"}
          />,
        );
      });
  };

  const submitCIDHandler = async (campaign_id: string) => {
    await enterCID({
      campaign_id,
      cid,
      sub_cids: [...(crmIntegration?.lead_tools_sub_cids || []), ...subCID],
    })
      .unwrap()
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Import Data Failed"}
          />,
        );
      });
  };

  const submitCRMHandler = async (
    campaign_id: string,
    import_contacts: boolean,
  ) => {
    let list_id: string | undefined;
    if (dataSourceList?.list_id && dataSourceList?.list_id.length > 0) {
      list_id = dataSourceList.list_id;
    }

    await importFromCRM({
      campaign_id,
      import_contacts,
      crm: dataSourceList?.crm!,
      list_id,
    })
      .unwrap()
      .then(() => {
        toast.success(
          <CustomToast
            type={"success"}
            text={"Company data uploading was successful."}
            title={"Upload Success"}
          />,
        );
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title={"Import Data Failed"}
          />,
        );
        setDataSourceList(null);
      });
  };

  const connectToCrmHandler = () => {
    getCRMUrl({
      campaign_id,
      action_type: "connect",
      redirect_path: location.pathname,
      crm: dataSourceList?.crm || crmIntegration?.integration || "",
    })
      .unwrap()
      .then((data) => {
        window.location.href = data.url;
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title="Connection Failed."
          />,
        ),
      );
  };

  const submitManuallyHandler = async (campaign_id: string) => {
    await enterCompanyManually({
      campaign_id,
      companies,
    })
      .unwrap()
      .then(() => {
        toast.success(
          <CustomToast
            type={"success"}
            text={"Company data uploading was successful."}
            title={"Upload Success"}
          />,
        );
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title={"Changes Were Not Saved"}
          />,
        );
      });
  };

  const submitHandler = async () => {
    if (isNeedConnect) {
      connectToCrmHandler();
      setIsLoading(false);
      return;
    }

    if (isMismatch) {
      setIsMismatchModalOpen(true);
      return;
    }

    setIsLoading(true);
    if (isFromManually) await submitManuallyHandler(campaign_id);
    if (isFromDesktop) await submitFromDesktopHandler(campaign_id);
    if (isFromCRM) await submitCRMHandler(campaign_id, true);
    if (isCID) await submitCIDHandler(campaign_id);

    if (!isFromDesktop) onDoneHandler();
  };

  const dis =
    !isNeedConnect &&
    !isMismatch &&
    !isFromDesktop &&
    !isFromCRM &&
    !isFromManually &&
    !(isCID && cid);

  const submitCRMisDisabled =
    currentMethod === methodLIst.crm &&
    crmIntegration?.is_connected &&
    ((crmIntegration?.is_connected && !isMismatch) ||
      !(isFromCRM || isMismatch));

  const submitDesktopIsDisabled =
    currentMethod === methodLIst.desktop && !isFromDesktop;

  return (
    <>
      <ReuseModal
        isHide={isVisible}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Re-Sync Data"
        maxWidth="540px"
        submitBtnText={
          isNeedConnect ||
          [methodLIst.desktop, methodLIst.manually, methodLIst.cid].includes(
            currentMethod,
          )
            ? "Re-Sync"
            : "Review & Sync"
        }
        submitBtnColor={
          isNeedConnect ||
          [methodLIst.desktop, methodLIst.manually, methodLIst.cid].includes(
            currentMethod,
          )
            ? "#1C3C6C"
            : "#F16300"
        }
        onSubmit={submitHandler}
        isLoading={isLoading}
        isDisabled={
          submitCRMisDisabled ||
          submitDesktopIsDisabled ||
          isLoading ||
          mismatches.is_syncing ||
          dis
        }
        isDisabledCancelBtn={isLoading}
      >
        <Stack gap="16px">
          <Stack flexDirection="row" gap="8px" flexWrap="nowrap">
            {navigationMethods.map(({ label, isDisable, hide }) => {
              if (hide) return null;
              return (
                <CustomButton
                  key={label}
                  onClick={() => setCurrentMethod(label)}
                  variant="text"
                  type="button"
                  sx={{
                    gap: 0,
                    textWrap: "nowrap",
                    color: `${label === currentMethod ? "#1C3C6C" : "#4C545B"}`,
                    cursor: `${label === currentMethod ? "auto" : "pointer"}`,
                    backgroundColor: `${
                      label === currentMethod ? "#F1F1F1" : "#F1F1F14D"
                    }`,
                    "&:hover": {
                      color: "#1C3C6C",
                      backgroundColor: "#F1F1F1",
                    },
                  }}
                  fullWidth
                  disabled={isDisable}
                >
                  {label}
                </CustomButton>
              );
            })}
          </Stack>
          {headers[currentMethod]}
          {methods[currentMethod]}
        </Stack>
      </ReuseModal>

      {isMismatchModalOpen && data && (
        <MismatchDataModal
          onClose={() => setIsOpen(false)}
          campaign_id={campaign_id}
          isOpen={isMismatchModalOpen}
          setIsOpen={setIsMismatchModalOpen}
          crm_mismatches={data.crm_mismatches}
          platform_mismatches={data.platform_mismatches}
        />
      )}
    </>
  );
};
